import type React from 'react'
import { Table } from '@/components/ui/table'
import { cn } from '@/lib/cn'
import { fmtMoney } from './format'
import type { CashFlowResponse } from '@/features/reporting/types'

interface Props {
  data: CashFlowResponse
  tableRef?: React.MutableRefObject<HTMLTableElement | null>
}

export function CashFlowReport({ data, tableRef }: Props) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table className="w-full min-w-[520px] text-sm" ref={tableRef}>
        <thead className="bg-primary-gradient text-xs uppercase tracking-wide text-primary-foreground">
          <tr>
            <th className="px-3 py-2 text-left">Item</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-muted/70 font-bold">
            <td colSpan={2} className="px-3 py-1.5">
              CASH FLOWS FROM OPERATING ACTIVITIES
            </td>
          </tr>
          <tr className="border-t">
            <td className="px-3 py-1.5 pl-6">Net Income</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.operating.netIncome)}</td>
          </tr>
          <tr className="bg-muted/40 font-semibold">
            <td colSpan={2} className="px-3 py-1 pl-6">
              Adjustments to reconcile net income to net cash
            </td>
          </tr>
          {data.operating.adjustments.map((adj, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-1.5 pl-8">{adj.lineItem}</td>
              <td className="px-3 py-1.5 text-right">{fmtMoney(adj.amount)}</td>
            </tr>
          ))}
          <tr className="border-t bg-muted/30 font-semibold">
            <td className="px-3 py-1.5">Total Adjustments</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.operating.adjustmentsTotal)}</td>
          </tr>
          <tr className="border-t-2 bg-muted font-bold">
            <td className="px-3 py-1.5">Net Cash from Operating Activities</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.operating.total)}</td>
          </tr>
          <tr>
            <td colSpan={2} className="py-1" />
          </tr>
          <tr className="bg-muted/70 font-bold">
            <td colSpan={2} className="px-3 py-1.5">
              CASH FLOWS FROM INVESTING ACTIVITIES
            </td>
          </tr>
          {data.investing.items.map((item, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-1.5 pl-6">{item.lineItem}</td>
              <td className="px-3 py-1.5 text-right">{fmtMoney(item.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 bg-muted font-bold">
            <td className="px-3 py-1.5">Net Cash from Investing Activities</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.investing.total)}</td>
          </tr>
          <tr>
            <td colSpan={2} className="py-1" />
          </tr>
          <tr className="bg-muted/70 font-bold">
            <td colSpan={2} className="px-3 py-1.5">
              CASH FLOWS FROM FINANCING ACTIVITIES
            </td>
          </tr>
          {data.financing.items.map((item, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-1.5 pl-6">{item.lineItem}</td>
              <td className="px-3 py-1.5 text-right">{fmtMoney(item.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 bg-muted font-bold">
            <td className="px-3 py-1.5">Net Cash from Financing Activities</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.financing.total)}</td>
          </tr>
          <tr>
            <td colSpan={2} className="py-1" />
          </tr>
          <tr className="bg-muted/70 font-bold">
            <td colSpan={2} className="px-3 py-1.5">
              NET CHANGE IN CASH
            </td>
          </tr>
          <tr className="border-t">
            <td className="px-3 py-1.5 pl-6">Beginning Cash</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.summary.beginningCash)}</td>
          </tr>
          <tr className="border-t">
            <td className="px-3 py-1.5 pl-6">Net Change in Cash</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.summary.netCashChange)}</td>
          </tr>
          <tr className="border-t-2 bg-muted font-bold">
            <td className="px-3 py-1.5">Ending Cash</td>
            <td className="px-3 py-1.5 text-right">{fmtMoney(data.summary.endingCash)}</td>
          </tr>
          <tr>
            <td colSpan={2} className="px-3 py-1.5">
              <span
                className={cn(
                  'font-semibold',
                  data.summary.cashReconciles ? 'text-success' : 'text-destructive',
                )}
              >
                {data.summary.cashReconciles
                  ? 'Cash reconciles to Balance Sheet'
                  : 'Cash does NOT reconcile to Balance Sheet'}
              </span>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  )
}
